// src/components/common/Header.js
import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import NotificationBar from 'components/common/NotificationBar';
import Logo from 'assets/brand-logo-compact.svg';
import { workspaceNavigation, workspaceLinkActive } from 'utils/workspaceNavigation';
import 'styles/pages/operations-workspace.css';

const Header = () => {
  const { currentUser, logout, loading } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const menuToggle = useRef(null);
  const menu = useRef(null);
  const closeMenu = () => {
    setIsMobileMenuOpen(false);
    menuToggle.current?.focus();
  };
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    menu.current?.querySelector('button')?.focus();
    const resized = () => { if (window.innerWidth >= 1024) setIsMobileMenuOpen(false); };
    window.addEventListener('resize', resized);
    return () => window.removeEventListener('resize', resized);
  }, [isMobileMenuOpen]);
  const menuKeyDown = (event) => {
    if (event.key === 'Escape') { event.preventDefault(); closeMenu(); return; }
    if (event.key !== 'Tab') return;
    const items = menu.current?.querySelectorAll('a[href],button:not([disabled])');
    if (!items?.length) return;
    const first = items[0], last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };


  useEffect(() => { setIsMobileMenuOpen(false); }, [location]);

  const handleLogout = () => {
    logout();
    setIsMobileMenuOpen(false);
  };

  const navLinks = workspaceNavigation(currentUser);
  const accountShell=['/dashboard','/profile','/BrowseTasks','/tasks'].includes(location.pathname);

  const renderNavLinks = () =>
    navLinks
      .map(l => (
        <NavLink
          key={l.to}
          to={l.to}
          className={({ isActive }) => `nav-link ${workspaceLinkActive(l, location, isActive) ? 'active' : ''}`}
          onClick={() => setIsMobileMenuOpen(false)}
        >
          {l.icon && <l.icon size={17} aria-hidden="true" />} {l.label}
        </NavLink>
      ));

  return (<>
    <header className={`header ${currentUser ? 'workspace-header' : ''}`}>
      <div className="container header-container">
        {/* Brand → Next.js Home at / */}
        <div className="header-brand">
          <a href="/" className="logo-link" onClick={(e) => { e.preventDefault(); window.location.href = '/'; }}>
            <img src={Logo} alt="Flytte og Fragttilbud" className="logo" />
            <span> </span><span className="brand-secondary"></span>
          </a>
        </div>

        {/* DESKTOP NAV */}
        <nav className="desktop-nav">
          {loading ? (
            <div className="auth-buttons">Loading...</div>
          ) : currentUser ? (
            <>
              {!accountShell&&<div className="nav-links-left">{renderNavLinks()}</div>}
              <div className="nav-user-section">
                <div className="user-profile">
                  <div className="user-avatar">
                    {currentUser.name?.charAt(0) || currentUser.email?.charAt(0) || 'A'}
                  </div>
                  <div className="user-details">
                    <span className="user-name">{currentUser.name || currentUser.email}</span>
                    <span className="user-role">{currentUser.role || "Bruger"}</span>
                  </div>
                </div>
                <button onClick={handleLogout} className="btn-logout">Log ud</button>
              </div>
            </>
          ) : (
            <div className="auth-buttons">
              <Link to="/login" className="btn btn-outline">Log ind</Link>
              <Link to="/register" className="btn btn-primary">Opret konto</Link>
            </div>
          )}
        </nav>

        {/* MOBIL NAV – altid vist (burger), indhold skifter efter login-tilstand */}
        <div className="mobile-nav-container">
          <button
            className="mobile-nav-toggle"
            ref={menuToggle}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-nav-menu"
            aria-label="Menu"
          >
            <span /><span /><span />
          </button>

          <nav ref={menu} hidden={!isMobileMenuOpen} onKeyDown={menuKeyDown} aria-label="Mobilnavigation" id="mobile-nav-menu" className={`mobile-nav-menu ${isMobileMenuOpen ? 'open' : ''}`}>
            <button
              className="mobile-menu-close"
              onClick={closeMenu}
              aria-label="Luk menu"
            >
              &times;
            </button>
            {loading ? (
              <div className="mobile-links">Loading...</div>
            ) : currentUser ? (
              <>
                <div className="mobile-user-info">
                  <div className="user-avatar large">
                    {currentUser.name?.charAt(0) || currentUser.email?.charAt(0) || 'U'}
                  </div>
                  <div>
                    <div className="user-name">{currentUser.name || currentUser.email}</div>
                    <div className="user-role">{currentUser.role || 'Bruger'}</div>
                  </div>
                </div>
                <div className="mobile-links">
                  {renderNavLinks()}
                </div>
                <button onClick={handleLogout} className="btn-logout-mobile">Log ud</button>
              </>
            ) : (
              <div className="mobile-links">
                <NavLink to="/login" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>Log ind</NavLink>
                <NavLink to="/register" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>Opret konto</NavLink>
              </div>
            )}
          </nav>
        </div>
      </div>
    </header>
    {currentUser && <NotificationBar />}
    </>
  );
};

export default Header;
